import { OryxGameController } from "@westpierstudio/core-module-oryx/game/OryxGameController";
import { showActivateFreeRoundFinishedDialog, showGamingLimitsReachedErrorDialog, showGenericCommsErrorDialog, showInsufficientFundsDialog } from "@westpierstudio/core-module-oryx/utils/errorHandling";
import { get, asBoolean } from "@westpierstudio/core-module-oryx/services/rgsdata/URLParsing";
import { EventEmitter } from "@westpierstudio/luma/events";
import { reset, Resettable } from "@westpierstudio/luma/utils/reset";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { DragonsVsPandasGameClient } from "game/DragonsVsPandasGameClient";
import { GameState, DragonsVsPandasGameModel, VSResultData, SpinCompleteWaiter } from "game/DragonsVsPandasGameModel";
import { DragonsVsPandasGameView } from "game/DragonsVsPandasGameView";
import { GameServices } from "./GameServices";
import { Background } from "components/Background";
import { ReelTopDisplay } from "components/ReelTopDisplay";
import { Reels } from "components/reels/Reels";
import { ReelEffects } from "components/reels/ReelEffects";
import { WinLines } from "components/winlines/WinLines";
import { WinLinesCount } from "components/winlines/WinLinesCount";
import { ReelSparkles } from "components/reels/ReelSparkles";
import { VSScreen } from "components/vsscreen/VSScreen";
import { TransitionPanel } from "components/vsscreen/TransitionPanel";
import { FreeSpinCounter } from "components/FreeSpinCounter";
import { FreeSpinsPrompt } from "components/FreeSpinsPrompt";
import { DarkenOverlay } from "components/DarkenOverlay";
import { VSRespinPanel } from "components/VSRespinPanel";
import { VSResultPanel } from "components/VSResultPanel";
import { FGTotalPanel } from "components/FGTotalPanel";
import { BigWin } from "@westpierstudio/core-module-oryx/components/bigwin";
import { SoundPlayer } from "components/SoundPlayer";
import { PortraitLogo } from "components/PortraitLogo";
import { BetConfig, BetConfigKey, OpenGameEvent } from "servertypes/gameevents/OpenGame";
import { SpinEvent } from "servertypes/gameevents/Spin";
import { VSSpinEvent } from "servertypes/gameevents/VSSpin";
import { FreeSpinEvent } from "servertypes/gameevents/FreeSpin";
import { SpinErrorEvent } from "servertypes/gameevents/SpinError";
import { GameEvent, GameRequest } from "servertypes/ServerEvents";
import { BetData, FreeSpinsCounter, LastEventData, NetPosition, OverlaySymbolData, ReelSetSymbols, TriggeringAction, VSOption, VSResult } from "servertypes/SharedTypes";
import { BonusSymbolPositions, OverlaySymbolState, SpinAction } from "components/reels/ReelsModel";
import { ScatterWin } from "components/winlines/WinlinesModel";
import { Signal } from "type-signals";
import { PeriodicInspectionEvent } from "@westpierstudio/core-module-oryx/services/comms/types/PeriodicInspection";
import { KSGLogo } from "components/KSGLogo";
import { GameDialogType } from "@westpierstudio/core-module-oryx/components/ui/dialog/DialogTypes";
import { DragonsVsPandasDebugGUI } from "gui/DragonsVsPandasDebugGUI";
import { SmallSpinEvent } from "servertypes/gameevents/SmallSpin";
import { MinTimeBetweenWagersAction } from "utils/MinTimeBetweenWagersAction";
import { SpinData, UIBetConfig } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/SlotGameUIView";
import { JackpotBowl } from "components/jackpotbowl/JackpotBowl";
import { toSpreadRTPEntries } from "./paytable/DragonVsPandasPaytableContentFactoryService";
import { invert, last, over } from "lodash";
import { scalePoint } from "@westpierstudio/luma/layout/DefaultLayout";
import { allSignals } from "@westpierstudio/luma/utils/signal";
import { Tween } from "@westpierstudio/luma/createjs";
import { ReelTopDragonPanda } from "components/ReelTopDragonPanda";
import { NoBonusMessage } from "components/NoBonusMessage";
import { game, layout } from "@westpierstudio/luma";

class GameSignals {
    vsChosen = new Signal();

    skipBigWin = new Signal();
    skipWinRollUp = new Signal();
    skipSpin = new Signal();
    skipVsDragonsSpin = new Signal();
    skipVsPandasSpin = new Signal();
    skipFreeSpinPrompt = new Signal();
    skipFreeSpinCounter = new Signal<(total: number) => void>();
    skipJackpot = new Signal();
    skipTransition = new Signal();
    skipTotalPanel = new Signal();
    skipResultPanel = new Signal();
    winlineLoopComplete = new Signal();
}

export class DragonsVsPandasGameController extends OryxGameController<DragonsVsPandasGameView, DragonsVsPandasGameModel> {
    private resetList: Resettable[] = [];
    private flow!: Flow;
    private background!: Background;
    private reelTopDisplay!: ReelTopDisplay;
    private reelTopDragonPanda!: ReelTopDragonPanda;
    private reels!: Reels;
    private reelEffects!: ReelEffects;
    private winlinesDragons!: WinLines;
    private winlinesPandas!: WinLines;
    private winlinesCountDragons!: WinLinesCount;
    private winlinesCountPandas!: WinLinesCount;
    private reelSparkles!: ReelSparkles;
    private freeSpinCounter!: FreeSpinCounter;
    private freeSpinsPrompt!: FreeSpinsPrompt;
    private darkenOverlay!: DarkenOverlay;
    private resultsDarkenOverlay!: DarkenOverlay;
    private vsRespinPanel!: VSRespinPanel;
    private vsResultPanelDragons!: VSResultPanel;
    private vsResultPanelPandas!: VSResultPanel;
    private fgTotalPanel!: FGTotalPanel;
    private jackpotBowl!: JackpotBowl;
    private vsScreen!: VSScreen;
    private ksgLogo!: KSGLogo;
    private bigWin!: BigWin;
    private vsTransition!: TransitionPanel;
    private soundplayer!: SoundPlayer;
    private portraitLogo!: PortraitLogo;
    private noBonusMessage!: NoBonusMessage;
    signals = new GameSignals();
    debugGUI?: DragonsVsPandasDebugGUI;

    constructor(protected override readonly services: GameServices, view: DragonsVsPandasGameView, model: DragonsVsPandasGameModel,
        gameEmitter: EventEmitter, protected override client: DragonsVsPandasGameClient) {
        super(services, view, model, gameEmitter, client);
    }

    override setup() {
        super.setup();

        this.resetList.push(this.soundplayer = new SoundPlayer(this.services, this.model));
        this.resetList.push(this.flow = new Flow(this.gameEmitter));

        this.setupComponents();
        this.resize();
        this.setupSkip();
        this.setupClient();
        this.setupGameSpecificDebugGUI();

        this.services.application.onTick.add(this.tick, this);
        this.services.layout.onResize.add(this.resize, this);
    }

    override initiateComms() {
        const showDebugUI = process.env.NODE_ENV === `production` ? false : asBoolean(get(`debugGUI`), true);
        if (showDebugUI) {
            this.setupDebugGUI();
        }

        this.client.establishConnection();
    }

    override reset() {

        super.reset();
        reset(this.resetList);
        this.client.establishConnection();
    }

    private setupClient(): void {

        this.client.signals.openGame.add(this.openGame, this);
        this.client.signals.smallSpin.add(this.smallSpin, this);
        this.client.signals.spin.add(this.spin, this);
        this.client.signals.vsSpin.add(this.vsSpin, this);
        this.client.signals.freeSpin.add(this.freeSpin, this);
        this.client.signals.spinError.add(this.spinError, this);
        this.client.signals.periodicInspection.add(this.periodicInspection, this);

        const openGameResponse = this.client.getLatestOpenGameResponse<OpenGameEvent>();
        if (openGameResponse) {
            this.openGame(openGameResponse);
        }
    }

    private setupDebugGUI(): void {
        const forcerGroups = this.services.resources.getSetting(`dev`).forcerGroups;
        const debugUI = this.debugGUI = new DragonsVsPandasDebugGUI(this.services, this.model);

        // delayed setup
        this.client.signals.openGame.once(event => debugUI.setupVersions(event.body.data.artifactVersions));
        this.client.signals.openGame.once(event => debugUI.setupRoundHistory(event.body.lastRoundId));
        this.client.adminSignals.forcerScenariosLoaded.once(scenarios => debugUI.setupForcer(scenarios, forcerGroups));

        // devui signals
        debugUI.signals.resetGame.add(() => this.resetGame.dispatch());
        debugUI.signals.force.add((value) => this.client.sendAdminForceRequest(value));
        debugUI.signals.setCurrencyCode.add(currencyCode => this.client.sendAdminSetCurrencyCodeRequest(currencyCode));
        this.client.signals.spin.add(event => debugUI.setRoundID(event.body.data.roundId));
        this.client.signals.smallSpin.add(event => debugUI.setRoundID(event.body.data.roundId));
    }

    private setupGameSpecificDebugGUI() {
        if (!this.debugGUI) {
            return;
        }
        this.debugGUI.customSignals.printFlow.add(() => this.flow.printQueue());
        this.debugGUI.customSignals.setFlowVerbose.add(value => this.flow.setVerbose(value));
    }

    private setupComponents(): void {

        this.view.rootGameContent.addChild(this.background = new Background(this.services));
        this.view.rootGameContent.addChild(this.resultsDarkenOverlay = new DarkenOverlay(this.services));
        this.view.rootGameContent.addChild(this.reels = new Reels(this.services));
        this.view.rootGameContent.addChild(this.freeSpinCounter = new FreeSpinCounter(this.services));
        this.view.rootGameContent.addChild(this.freeSpinsPrompt = new FreeSpinsPrompt(this.services));
        this.view.rootGameContent.addChild(this.darkenOverlay = new DarkenOverlay(this.services));
        this.view.rootGameContent.addChild(this.vsRespinPanel = new VSRespinPanel(this.services));
        this.view.rootGameContent.addChild(this.portraitLogo = new PortraitLogo(this.services));
        this.view.rootGameContent.addChild(this.fgTotalPanel = new FGTotalPanel(this.services));
        this.view.rootGameContent.addChild(this.jackpotBowl = new JackpotBowl(this.services));
        this.view.rootGameContent.addChild(this.vsResultPanelDragons = new VSResultPanel(this.services, `dragons`));
        this.view.rootGameContent.addChild(this.vsResultPanelPandas = new VSResultPanel(this.services, `pandas`));
        this.view.rootGameContent.addChild(this.vsScreen = new VSScreen(this.services));
        this.view.rootGameContent.addChild(this.ksgLogo = new KSGLogo(this.services, this.services.layout));
        this.view.rootGameContent.addChild(this.bigWin = new BigWin(this.services));
        this.view.rootGameContent.addChild(this.vsTransition = new TransitionPanel(this.services));
        this.view.rootGameContent.addChild(this.noBonusMessage = new NoBonusMessage(this.services));

        const reelsLayout = this.model.reels.setupLayout();

        this.reels.setup(reelsLayout);
        //behind reels
        this.reels.addChildAt(this.reelTopDragonPanda = new ReelTopDragonPanda(this.services), 0);
        this.reels.addChildAt(this.reelEffects = new ReelEffects(this.services), 1);
        //overlay reels
        this.reels.addChild(this.reelTopDisplay = new ReelTopDisplay(this.services));
        this.reels.addChild(this.winlinesCountDragons = new WinLinesCount(this.services, `dragons`));
        this.reels.addChild(this.winlinesCountPandas = new WinLinesCount(this.services, `pandas`));
        this.reels.addChild(this.winlinesDragons = new WinLines(this.services));
        this.reels.addChild(this.winlinesPandas = new WinLines(this.services));
        this.reels.addChild(this.reelSparkles = new ReelSparkles(this.services));

        this.resetList.push(this.background);
        this.resetList.push(this.reelTopDisplay);
        this.resetList.push(this.reels);
        this.resetList.push(this.reelEffects);
        this.resetList.push(this.winlinesDragons);
        this.resetList.push(this.winlinesPandas);
        this.resetList.push(this.winlinesCountDragons);
        this.resetList.push(this.winlinesCountPandas);
        this.resetList.push(this.reelSparkles);
        this.resetList.push(this.freeSpinCounter);
        this.resetList.push(this.freeSpinsPrompt);
        this.resetList.push(this.darkenOverlay);
        this.resetList.push(this.resultsDarkenOverlay);
        this.resetList.push(this.vsRespinPanel);
        this.resetList.push(this.vsResultPanelDragons);
        this.resetList.push(this.vsResultPanelPandas);
        this.resetList.push(this.fgTotalPanel);
        this.resetList.push(this.jackpotBowl);
        this.resetList.push(this.vsScreen);
        this.resetList.push(this.bigWin);
        this.resetList.push(this.vsTransition);
        this.resetList.push(this.reelSparkles);
        this.resetList.push(this.noBonusMessage);

        this.winlinesDragons.signals.winLineComplete.add(() => {
            this.winLineLoopDragons(false);
        }, this);
        this.winlinesPandas.signals.winLineComplete.add(() => {
            this.winLineLoopPandas();
        }, this);

        this.reels.signals.reelStop.add(this.reelStop, this);
        this.reels.signals.reelStopSound.add(() => this.soundplayer.reelStop());
        this.reels.signals.reelStopPotCoin.add(this.reelStopPotCoin, this);

        this.reels.signals.coinHit1Sound.add(() => this.soundplayer.coinHit1());
        this.reels.signals.coinHit2Sound.add(() => this.soundplayer.coinHit2());
        this.reels.signals.coinHit3Sound.add(() => this.soundplayer.coinHit3());

        this.reels.signals.jumpingWildSound.add(() => this.soundplayer.jumpingWild());
        this.reels.signals.jumpingWildHitSound.add(this.jumpingWildHit, this);

        this.reels.signals.sparkleRequest.add(this.reelSparkle, this);

        this.reels.signals.dragonRoar.add(this.dragonRoar, this);
        this.reels.signals.pandaRoar.add(this.pandaRoar, this);

        this.freeSpinCounter.signals.totalCounterIncrement.add(() => this.soundplayer.fgIncrement());

        this.jackpotBowl.signals.countUpComplete.add(() => this.jackpotBowl.endWinLoop());
        this.jackpotBowl.signals.coinMoveComplete.add(() => this.jackpotCoinMoveComplete());
        this.jackpotBowl.signals.coinCollision.add(() => this.soundplayer.coinBowlTouch());
        this.jackpotBowl.signals.coinBowlTouch.add(() => this.soundplayer.coinBowlTouch());

        this.vsScreen.signals.dragonClick.add(() => this.onVSScreenPick(VSOption.DRAGON));
        this.vsScreen.signals.pandaClick.add(() => this.onVSScreenPick(VSOption.PANDA));

        this.vsTransition.signals.transitionInSound.add(() => this.soundplayer.transitionIn());
        this.vsTransition.signals.transitionOutSound.add(() => this.soundplayer.transitionOut());
        this.vsTransition.signals.dragonRoar.add(() => this.soundplayer.dragonRoar(false));
        this.vsTransition.signals.pandaRoar.add(() => this.soundplayer.pandaRoar(false));
        this.vsTransition.signals.animationInComplete.add(() => this.jackpotBowl.setInteractive(false));
        this.vsTransition.signals.animationOutComplete.add(() => this.onTransitionComplete());

        this.vsResultPanelDragons.signals.tearStart.add(() => this.soundplayer.vsTearDragon());
        this.vsResultPanelPandas.signals.tearStart.add(() => this.soundplayer.vsTearPanda());
        this.vsResultPanelDragons.signals.winSounds.add(() => { this.soundplayer.vsWinDragon(); this.soundplayer.summaryWindowIn(); });
        this.vsResultPanelPandas.signals.winSounds.add(() => { this.soundplayer.vsWinPanda(); this.soundplayer.summaryWindowIn(); });
        this.vsResultPanelDragons.signals.panelIn.add(() => this.onResultPanelsIn());

        this.fgTotalPanel.signals.winSummarySound.add(() => this.soundplayer.summaryWindowIn());

        this.bigWin.signals.countupComplete.add(() => this.clearSkip());
        this.bigWin.signals.sounds.playCountup.add(() => this.soundplayer.bigWinCountUp());
        this.bigWin.signals.sounds.stopCountup.add(() => this.soundplayer.stopBigWinCountUp());
        this.bigWin.signals.sounds.playSting.add(() => this.soundplayer.bigWinSting());
    }

    private setupSkip(): void {

        this.signals.skipBigWin.add(() => {

            this.skipBigWin();
            this.skipWinRollUp();
        });

        this.signals.skipFreeSpinPrompt.add(this.skipFreeSpinPrompt, this);

        if (this.services.rgsData.turboPlay) {

            this.signals.skipSpin.add(() => {
                this.slamStop(this.model.reels.dragonsSpinResult as ReelSetSymbols, this.model.reels.pandasSpinResult as ReelSetSymbols);
            });

            this.signals.skipVsDragonsSpin.add(() => {
                this.slamStopDragonsReels(this.model.reels.dragonsSpinResult as ReelSetSymbols);
            });

            this.signals.skipVsPandasSpin.add(() => {
                this.slamStopPandasReels(this.model.reels.pandasSpinResult as ReelSetSymbols);
            });

            this.signals.skipWinRollUp.add(() => this.skipWinRollUp());
            this.signals.skipFreeSpinCounter.add((total: number) => this.skipFreeSpinCounter(total));
            this.signals.skipJackpot.add(() => this.skipJackpot());
            this.signals.skipTransition.add(() => this.skipTransition());
            this.signals.skipTotalPanel.add(() => this.skipTotalPanel());
            this.signals.skipResultPanel.add(() => this.skipResultPanel());
        }
    }

    private reelStop(reelIndex: number): void {
        this.reelSparkles.reelStopped(reelIndex);
    }

    private reelStopPotCoin(reelIndex: number): void {

        const overlayResult = this.model.reels.getOverlayResultForReel(reelIndex);
        let symbolIndex = -1;

        for (const [i, entry] of overlayResult.entries()) {

            if (entry !== `E`) {
                symbolIndex = i;
                break;
            }
        }

        if (symbolIndex !== -1) {
            const symbolPosition = this.reels.getSymbolGlobalPosition(reelIndex, symbolIndex, this.model.reels.layout);
            this.jackpotBowl.spawnNewCoin(symbolPosition);
            this.soundplayer.coinFlying();
        }
    }

    private reelSparkle(reelIndex: number, distance: number, reelSpeed?: number | undefined): void {

        if (distance === 5) {
            this.soundplayer.overlayPassby();
        }
        this.reelSparkles.createEffect(reelIndex, distance, reelSpeed);
    }

    private jumpingWildHit(hitNum: number, multiplier: number): void {
        this.soundplayer.jumpingWildHitMult(multiplier);
        this.soundplayer.jumpingWildHit(hitNum);
    }

    private jackpotCoinMoveComplete(): void {
        this.soundplayer.coinFlyingTerm();
    }

    private dragonRoar(inAnimLoop: boolean): void {
        if (this.onReels()) {
            this.soundplayer.dragonRoar(inAnimLoop);
        }
    }

    private pandaRoar(inAnimLoop: boolean): void {
        if (this.onReels()) {
            this.soundplayer.pandaRoar(inAnimLoop);
        }
    }

    private onVSScreenPick(pick: VSOption): void {

        this.model.enteringVS = false;
        this.soundplayer.vsPick();
        this.reels.setupNormalDividers();

        if (pick === VSOption.DRAGON) {
            this.soundplayer.vsPickDragon();
            this.soundplayer.dragonRoar(false);
            this.reels.setupVSDragonFlames();
            this.winlinesCountDragons.enableFlames();
            this.winlinesCountPandas.disableFlames();
        } else {
            this.soundplayer.vsPickPanda();
            this.soundplayer.pandaRoar(false);
            this.reels.setupVSPandaFlames();
            this.winlinesCountDragons.disableFlames();
            this.winlinesCountPandas.enableFlames();
        }

        this.model.vsPickIndex = pick;
        this.darkenOverlay.enable(pick);
        this.reelTopDisplay.setVSEffects(pick);
        this.vsScreen.animateOut();
        this.jackpotBowl.setInteractive(true);

        this.signals.vsChosen.dispatch();
    }

    private onResultPanelsIn(): void {
        const { pick } = this.model.vsResultData as VSResultData;
        if (pick === VSOption.DRAGON) {
            this.resultsDarkenOverlay.enable(VSOption.PANDA);
            this.reelTopDragonPanda.tintDragon();
        } else {
            this.resultsDarkenOverlay.enable(VSOption.DRAGON);
            this.reelTopDragonPanda.tintPanda();
        }
    }

    private processFreeRounds(event: OpenGameEvent) {
        if (!event.body.data.FREE_ROUNDS || !event.body.data.FREE_ROUNDS.length) {
            return;
        }
        const freeRound = event.body.data.FREE_ROUNDS[0];

        if (freeRound.type !== `NUM_ROUNDS`) {
            console.error(`Unsupported FREE_ROUND type "${freeRound.type}"`);
            this.client.sendActivateFreeRoundRequest(freeRound.id, `DECLINE`);
            return;
        }

        this.gameDialogs.show({
            type: GameDialogType.FREE_ROUNDS_OFFER,
            resumingFreeRounds: freeRound.status === `ACTIVE`,
            availableBonusSpins: freeRound.data.numLeft,
            accept: () => {
                if (freeRound.status === `PENDING`) {
                    this.client.sendActivateFreeRoundRequest(freeRound.id, `ACCEPT`);
                }
                this.gameUI?.freeRoundsStarted(freeRound.conf.betAmount, freeRound.data.numLeft);
            }
        });

        this.model.setFreeRoundData(freeRound);
    }

    private openGame(event: OpenGameEvent): void {

        const { rtp, betConfigs, betConfig } = event.body.data;
        if (typeof rtp !== `number`) {
            if (betConfigs)
                this.services.paytableContentFactory.setSpreadRTPEntries([
                    ...(rtp.SMALL_SPIN ? toSpreadRTPEntries(rtp.SMALL_SPIN, betConfigs.SMALL_SPIN) : []),
                    ...toSpreadRTPEntries(rtp.SPIN, betConfigs.SPIN)
                ]);
            else if (betConfig) {
                this.services.paytableContentFactory.setSpreadRTPEntries(toSpreadRTPEntries(rtp.SPIN, betConfig));
            }
        }

        this.processFreeRounds(event);
        this.model.processOpenGameEvent(event);

        this.services.paytableContentFactory.setOpenGameData(event, this.model.rtpType);

        const { data, lastEvent, lastRoundId } = event.body;
        const { dragonsDefaultReelStrips, pandasDefaultReelStrips, dragonsDefaultReels, pandasDefaultReels } = data;
        const { paylines } = this.services.resources.getSetting(`dev`);

        this.flow.reset();
        this.setupGameUI(event, lastEvent?.data, lastRoundId);

        if (lastEvent) {
            this.resumeGame(lastEvent.data, lastEvent.event);
        } else {
            this.model.state = GameState.IDLE;
            this.setBaseGameModeVisuals();
        }

        this.reels.setReelStrips(dragonsDefaultReelStrips, pandasDefaultReelStrips);
        this.reels.setReelSymbols(dragonsDefaultReels, pandasDefaultReels, this.model.reels.overlaySymbolState);

        this.winlinesDragons.setup(paylines, this.model.reels.layout, `dragonsWinlines`);
        this.winlinesPandas.setup(paylines, this.model.reels.layout, `pandasWinlines`);

        this.services.gameBridge.sendGameBridgeEvent({ event: `setBalance`, payload: { balance: event.body.balance / this.services.currency.currencyConfig.minorDivisor } });
        this.services.gameBridge.sendGameBridgeEvent({ event: `gameReady` });
        this.services.gameBridge.sendGameBridgeEvent({ event: this.services.audio.soundEnabled ? `soundOn` : `soundOff` });
        this.services.audio.onSoundsLoaded.add(() => this.soundplayer.onSoundsLoaded());

        this.loadingScreenHide.dispatch();
        this.reelTopDisplay.setIdle();

        this.flow.run();
    }

    private resumeGame(data: LastEventData, lastEvent?: GameEvent): void {

        const { betData, nextActions, triggeringAction, gameplay } = data;

        this.model.haveEnteredFreeSpins = true;

        if (nextActions[0] === GameRequest.VS_SPIN) {

            this.vsScreen.enable();
            this.jackpotBowl.setInteractive(false);
            this.vsScreen.show(this.model.vsHistoryData);
            this.vsScreen.flashLoop();
            this.soundplayer.vsMusic();
            this.soundplayer.playVSOnLoad = true;
            this.soundplayer.vsPickPromptLoop();
            this.setVSGameMode();

            this.flow.addSignalBlock(this.signals.vsChosen)
                .addCallback(() => {
                    this.client.sendVSSpinRequest(betData, this.model.vsPickIndex as VSOption, this.model.freeRoundId);
                });

        } else if (nextActions[0] === GameRequest.FREE_SPIN) {

            const freeSpinsCounter = gameplay.freeSpinsCounter;
            const entering = triggeringAction.type === `SPIN` || (freeSpinsCounter ? (triggeringAction.type === `VS_SPIN` && freeSpinsCounter.freeSpinsRemaining === 88) : false);

            this.soundplayer.fgMusic();
            this.soundplayer.playFGOnLoad = true;

            this.addSetFreeSpinsGameModeToFlow(entering, lastEvent === `VS_SPIN`);
            if (entering) this.addCountUpToTotalFreeSpinsToFlow(freeSpinsCounter ? freeSpinsCounter : {freeSpinsRemaining: 88, freeSpinsTotal: 88});

            this.flow.addCallback(() => {
                this.client.sendFreeSpinRequest(betData, this.model.freeRoundId);
            });
        } else {
            this.setBaseGameModeVisuals();
        }
    }

    private smallSpin(event: SmallSpinEvent): void {

        this.noBonusMessage.disappear();

        const { freeRounds: freeRound } = event.body.data;

        if (freeRound) {

            this.model.setFreeRoundData(freeRound);
            if (freeRound.type === `NUM_ROUNDS`) {
                this.gameUI?.freeRoundsSpinsLeftChanged(freeRound.data.numLeft);
            }
        }

        this.services.gameBridge.sendGameBridgeEvent({ event: `gameBusy` });

        this.model.processSpinEvent(event);

        const { balance, balanceVersion, elapsedTimeInMillis, netPosition } = event.body;
        const { accumulatedMoneyWon, balanceAfterBetData, betData, nextActions, roundId } = event.body.data;
        const { oldDragonsReels, oldPandasReels } = event.body.data.gameplay;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);
        this.updateBalance(balanceAfterBetData.balanceAfterBet, balanceAfterBetData.balanceAfterBetVersion);

        const spinAction = SpinAction.SPIN;

        this.flow.reset();
        this.addReelSpinToFlow(oldDragonsReels[0], oldPandasReels[0], spinAction, betData.playerBet);
        this.addJumpingWildsToFlow();
        this.addSetWinDisplayConfigToFlow(spinAction);
        this.addBigWinToFlow(event, spinAction);
        this.addWinsToFlow(event, spinAction);

        this.flow.add(new MinTimeBetweenWagersAction(this.services.rgsData.minTimeBetweenWagers));

        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, balance, balanceVersion, false, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private spin(event: SpinEvent): void {

        this.noBonusMessage.disappear();

        const { freeRounds: freeRound } = event.body.data;

        if (freeRound) {

            this.model.setFreeRoundData(freeRound);
            if (freeRound.type === `NUM_ROUNDS`) {
                this.gameUI?.freeRoundsSpinsLeftChanged(freeRound.data.numLeft);
            }
        }

        this.services.gameBridge.sendGameBridgeEvent({ event: `gameBusy` });

        this.model.processSpinEvent(event);

        const { balance, balanceVersion, elapsedTimeInMillis, netPosition } = event.body;
        const { accumulatedMoneyWon, balanceAfterBetData, betData, nextActions, roundId } = event.body.data;
        const { oldDragonsReels, oldPandasReels } = event.body.data.gameplay;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);
        this.updateBalance(balanceAfterBetData.balanceAfterBet, balanceAfterBetData.balanceAfterBetVersion);

        const spinAction = SpinAction.SPIN;

        this.flow.reset();
        this.addReelSpinToFlow(oldDragonsReels[0], oldPandasReels[0], spinAction, betData.playerBet);
        this.addJumpingWildsToFlow();
        this.addSetWinDisplayConfigToFlow(spinAction);
        this.addJackpotToFlow(event);
        this.addBigWinToFlow(event, spinAction);
        this.addWinsToFlow(event, spinAction);
        this.addVSWinToFlow();
        this.addFreeSpinsWinToFlow();

        if (!nextActions) {
            this.flow.add(new MinTimeBetweenWagersAction(this.services.rgsData.minTimeBetweenWagers));
        }

        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, balance, balanceVersion, false, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private vsSpin(event: VSSpinEvent): void {

        this.model.processVSSpinEvent(event);
        this.model.state = GameState.VS_SPIN;

        const { balance, balanceVersion, elapsedTimeInMillis, netPosition } = event.body;
        const { accumulatedMoneyWon, betData, nextActions, roundId } = event.body.data;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);

        const { pick } = this.model.vsResultData as VSResultData;
        if (pick === VSOption.DRAGON) {
            this.soundplayer.vsReelsDragon();
        } else {
            this.soundplayer.vsReelsPanda();
        }

        const spinAction = SpinAction.VS_SPIN;

        this.flow.reset();
        this.addVSSpinToFlow(event);
        this.flow.addCallback(() => { this.soundplayer.vsReelsTerm(); });
        this.addVSResultsToFlow();
        this.addSetWinDisplayConfigToFlow(spinAction);
        this.addJackpotToFlow(event, true);
        this.addBigWinToFlow(event, spinAction);
        this.addWinsToFlow(event, spinAction);

        if (nextActions) {
            /*
                Freespins cannot be won during vs
                This can either be a vs spin during freeSpins so we are returning to freespins
                or a vsSpin prior to freespins where the same set of scatters trigger both.
            */
            this.addVSTransitionToFlow(true);
            this.addSetFreeSpinsGameModeToFlow(this.model.haveEnteredFreeSpins === false, true);
        } else {

            const { freeGamesState } = this.model.reels.bonusTriggeringState;
            if (freeGamesState) {
                this.addVSTransitionToFlow(true);
                this.addSetFreeSpinsGameModeToFlow(false, true);

                const { wins, pick } = this.model.vsResultData as VSResultData;
                if (pick === VSOption.DRAGON) {
                    this.addRestoreScatterWinToFlow(wins.dragon, false);
                } else {
                    this.addRestoreScatterWinToFlow(wins.panda, false);
                }

                this.flow.addCallback(() => {
                    this.flow.addSignalBlock(this.signals.winlineLoopComplete);
                    this.addTotalPanelToFlow(accumulatedMoneyWon, true);

                    this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, balance, balanceVersion, true, nextActions);
                    this.addSessionCompleteGameBridgeToFlow(nextActions);
                });

                this.flow.run();

                return;
            } else {
                this.addVSTransitionToFlow();
                this.addRestoreScatterWinToFlow(accumulatedMoneyWon);

                if (this.vsTransition.getInAnim()) {
                    this.flow.addSignalBlock(this.vsTransition.signals.animationOutComplete);
                }

                this.flow.addCallback(() => {
                    this.clearSkip();
                });
            }
        }

        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, balance, balanceVersion, true, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private freeSpin(event: FreeSpinEvent): void {

        this.model.processFreeSpinEvent(event);
        this.model.state = GameState.FREE_SPIN;

        const { balance, balanceVersion, elapsedTimeInMillis, netPosition } = event.body;
        const { accumulatedMoneyWon, betData, nextActions, roundId } = event.body.data;
        const { freeSpinsCounter, oldDragonsReels, oldPandasReels } = event.body.data.gameplay;

        this.syncSessionElapsedTimeIfExists(elapsedTimeInMillis);
        this.gameUI?.updateTransactionId(roundId);

        const spinAction = SpinAction.FREE_SPIN;

        this.flow.reset();

        if (!this.model.haveEnteredFreeSpins) {

            this.model.haveEnteredFreeSpins = true;
            this.addCountUpToTotalFreeSpinsToFlow(freeSpinsCounter);
        } else if (this.vsTransition.getInAnim()) {
            this.flow.addSignalBlock(this.vsTransition.signals.animationOutComplete);
        }

        this.addSetFreeSpinsToFlow(freeSpinsCounter);
        this.addReelSpinToFlow(oldDragonsReels[0], oldPandasReels[0], spinAction, betData.playerBet);
        this.addJumpingWildsToFlow();
        this.addSetWinDisplayConfigToFlow(spinAction);
        this.addJackpotToFlow(event);
        this.addBigWinToFlow(event, spinAction);
        this.addWinsToFlow(event, spinAction);
        this.addVSWinToFlow();

        if (!nextActions) {
            this.addTotalPanelToFlow(accumulatedMoneyWon);
        }

        this.addNextActionsHandlingToFlow(betData, accumulatedMoneyWon, netPosition, balance, balanceVersion, true, nextActions);
        this.addSessionCompleteGameBridgeToFlow(nextActions);
        this.flow.run();
    }

    private addTotalPanelToFlow(accumulatedMoneyWon: number, endedOnVS: boolean = false): void {
        this.flow.addCallback(() => {
            this.fgTotalPanel.setValueText(accumulatedMoneyWon);
            this.fgTotalPanel.show();
            this.freeSpinCounter.fadeOut();
            this.setSkip(this.signals.skipTotalPanel);
        })
            .addSignalBlock(this.fgTotalPanel.signals.showComplete)
            .addCallback(() => {
                this.soundplayer.fgMusicTerm();
                this.vsTransition.playCloseTransition();
                this.setSkip(this.signals.skipTransition);
            })
            .addSignalBlock(this.vsTransition.signals.animationInComplete)
            .addCallback(() => {
                this.addSetWinDisplayConfigToFlow(SpinAction.SPIN);
                this.model.applyBonusOverlaySymbols();
            });

        this.addRestoreScatterWinToFlow(accumulatedMoneyWon, true, endedOnVS);

        this.flow.addCallback(() => {
            this.flow.addCallback(() => {
                this.clearSkip();
            });
        });
    }

    private addReelSpinToFlow(dragonsSpinResult: ReelSetSymbols, pandasSpinResult: ReelSetSymbols, spinAction: SpinAction, playerBet: number): void {

        this.flow.addCallback(() => {

            const { overlaySymbolState } = this.model.reels;

            const spinType = this.model.getSpinType(spinAction);
            const spinConfig = this.model.reels.setSpinConfigForSpinType(
                spinType,
                this.model.anticipation
            );

            if (spinConfig.logSpinTime) {
                this.model.reels.startTime = Date.now();
            }

            this.reels.setSpinConfig(spinConfig);
            this.reels.spinAllReels(dragonsSpinResult, pandasSpinResult, overlaySymbolState, playerBet);

            if (spinConfig.instantSlam) {
                this.slamStop(dragonsSpinResult, pandasSpinResult);
            }

            this.setSkip(this.signals.skipSpin);
        })
            .addSignalBlock(this.reels.signals.spinComplete)
            .addCallback(() => {

                if (this.model.reels.activeSpinConfig.logSpinTime) {
                    console.log(`Spin Time(ms): `, Date.now() - this.model.reels.startTime);
                }

                this.clearSkip();
            });
    }

    private addVSSpinToFlow(event: VSSpinEvent): void {

        const { overlaySymbols } = event.body.data.gameplay;
        const { dragonsVSSpinResults, pandasVSSpinResults } = this.model.reels;

        if (this.vsScreen.visible) {
            this.flow.addSignalBlock(this.vsScreen.signals.animateOutComplete);
        }

        this.addVSInitialSpinToFlow(dragonsVSSpinResults[0], pandasVSSpinResults[0]);

        if (dragonsVSSpinResults.length > 1) {
            this.addVSRespinsDragonsToFlow(dragonsVSSpinResults, overlaySymbols);

        } else if (pandasVSSpinResults.length > 1) {
            this.addVSRespinsPandasToFlow(pandasVSSpinResults, overlaySymbols);
        }
    }

    private addVSInitialSpinToFlow(dragonsResult: ReelSetSymbols, pandasResult: ReelSetSymbols): void {

        this.flow.addCallback(() => {

            const { overlaySymbolState } = this.model.reels;

            const spinType = this.model.getSpinType(SpinAction.VS_SPIN);
            const spinConfig = this.model.reels.setSpinConfigForSpinType(
                spinType,
                this.model.anticipation
            );

            this.reels.setSpinConfig(spinConfig);
            this.reels.spinAllReels(dragonsResult, pandasResult, overlaySymbolState);

            if (spinConfig.instantSlam) {
                this.slamStop(dragonsResult, pandasResult);
            }

            this.setSkip(this.signals.skipSpin);
        })
            .addSignalBlock(this.reels.signals.spinComplete)
            .addCallback(() => {
                this.clearSkip();
            });
    }

    private addVSRespinsDragonsToFlow(results: ReelSetSymbols[], overlaySymbols: OverlaySymbolData[]): void {

        for (const [i, result] of results.entries()) {

            this.flow.addCallback(() => {
                this.soundplayer.vsRespin();
                this.vsRespinPanel.show(VSOption.DRAGON);
            })
                .addSignalBlock(this.vsRespinPanel.signals.showComplete)
                .addCallback(() => {

                    this.model.reels.dragonsSpinResult = result;

                    if (i === results.length - 1) {
                        this.model.reels.setOverlaySymbolState(overlaySymbols);
                        this.reels.spinDragonsReels(result, this.model.reels.overlaySymbolState.dragons);

                    } else {
                        this.reels.spinDragonsReels(result, this.model.reels.overlaySymbolEmptySet);
                    }

                    this.setSkip(this.signals.skipVsDragonsSpin);
                })
                .addSignalBlock(this.reels.signals.spinComplete);
        }
    }

    private addVSRespinsPandasToFlow(results: ReelSetSymbols[], overlaySymbols: OverlaySymbolData[]): void {

        for (const [i, result] of results.entries()) {

            this.flow.addCallback(() => {
                this.soundplayer.vsRespin();
                this.vsRespinPanel.show(VSOption.PANDA);
            })
                .addSignalBlock(this.vsRespinPanel.signals.showComplete);
            this.flow.addCallback(() => {

                this.model.reels.pandasSpinResult = result;

                if (i === results.length - 1) {
                    this.model.reels.setOverlaySymbolState(overlaySymbols);
                    this.reels.spinPandasReels(result, this.model.reels.overlaySymbolState.pandas);

                } else {
                    this.reels.spinPandasReels(result, this.model.reels.overlaySymbolEmptySet);
                }

                this.setSkip(this.signals.skipVsPandasSpin);
            })
                .addSignalBlock(this.reels.signals.spinComplete);
        }
    }

    private addVSResultsToFlow(): void {

        this.flow.addCallback(() => {

            const { result, wins, pick } = this.model.vsResultData as VSResultData;

            if (result === VSResult.DRAGON_WIN) {

                this.vsResultPanelDragons.showWin(wins.dragon, pick === VSOption.DRAGON);
                this.vsResultPanelPandas.showLoss(wins.panda, pick === VSOption.PANDA);

            } else if (result === VSResult.PANDA_WIN) {

                this.vsResultPanelDragons.showLoss(wins.dragon, pick === VSOption.DRAGON);
                this.vsResultPanelPandas.showWin(wins.panda, pick === VSOption.PANDA);

            } else {

                this.soundplayer.vsDraw();
                this.soundplayer.summaryWindowIn();
                this.vsResultPanelDragons.showDraw(wins.dragon);
                this.vsResultPanelPandas.showDraw(wins.panda);
            }

            this.setSkip(this.signals.skipResultPanel);
        })
            .addSignalBlock(this.vsResultPanelPandas.signals.moveStart)
            .addCallback(() => {
                this.reelTopDisplay.fadeLogos();
            })
            .addSignalBlock(this.vsResultPanelPandas.signals.showComplete)
            .addCallback(() => {
                this.clearSkip();
            });
    }

    private addJumpingWildsToFlow(restoreMode: boolean = false): void {

        const { dragons, pandas } = this.model.reels.jumpingWilds;

        if (dragons.length > 0 || pandas.length > 0) {

            this.flow.addCallback(() => {
                this.reels.setAboveReelsOverlayMode(this.model.reels.overlaySymbolState, this.model.reels.layout);
            });
        }

        this.reels.resetJumpingWildHitData();

        if (dragons.length > 0) {
            this.flow.addCallback(() => {
                this.reels.showJumpingWildsSet(dragons, this.model.reels.layout, restoreMode);
            })
                .addSignalBlock(this.reels.signals.jumpingWildsSetComplete);
        }

        if (pandas.length > 0) {
            this.flow.addCallback(() => {
                this.reels.showJumpingWildsSet(pandas, this.model.reels.layout, restoreMode);
            })
                .addSignalBlock(this.reels.signals.jumpingWildsSetComplete);
        }

        if (dragons.length > 0 || pandas.length > 0) {

            this.flow.addCallback(() => {
                this.reels.setOnReelsOverlayMode();
            });
        }
    }

    private addBigWinToFlow(event: SpinEvent | SmallSpinEvent | VSSpinEvent | FreeSpinEvent, spinAction: SpinAction): void {

        if (this.model.bigWin) {

            const { balance, balanceVersion } = event.body;
            const { accumulatedMoneyWon, betData, totalMoneyWon } = event.body.data;

            this.flow.addCallback(() => {

                if (spinAction === SpinAction.SPIN) {
                    this.model.state = GameState.SHOW_BIG_WIN;

                } else if (spinAction === SpinAction.VS_SPIN) {
                    this.model.state = GameState.VS_SPIN_SHOW_BIG_WIN;

                } else {
                    this.model.state = GameState.FREE_SPIN_SHOW_BIG_WIN;
                }

            })
                .addDelay(3000)
                .addCallback(() => {
                    this.bigWin.start(betData.playerBet, totalMoneyWon);

                    this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, false);
                    this.setSkip(this.signals.skipBigWin);
                })
                .addSignalBlock(this.bigWin.signals.complete)
                .addCallback(() => {
                    this.reelEffects.disable();
                });
        }
    }

    private addSetWinDisplayConfigToFlow(spinAction: SpinAction): void {

        this.flow.addCallback(() => {

            const spinType = this.model.getSpinType(spinAction);
            const winDisplayConfig = this.model.setWinDisplayConfigForSpinType(spinType);

            this.winlinesDragons.setWinDisplayConfig(winDisplayConfig);
            this.winlinesPandas.setWinDisplayConfig(winDisplayConfig);
        });
    }

    private addWinsToFlow(event: SpinEvent | SmallSpinEvent | VSSpinEvent | FreeSpinEvent, spinAction: SpinAction): void {

        const { balance, balanceVersion } = event.body;
        const { accumulatedMoneyWon, betData, gameplay, totalMoneyWon, nextActions } = event.body.data;

        let winsToShow = false;

        //This is required as the BE is currently sending back wins for the non-picked side of the vs.
        if (spinAction === SpinAction.VS_SPIN) {

            const { result } = this.model.vsResultData as VSResultData;

            winsToShow = (result === VSResult.DRAGON_WIN) ?
                gameplay.dragonsPaylineWins.length > 0 :
                gameplay.pandasPaylineWins.length > 0;
        } else {
            winsToShow = (gameplay.dragonsPaylineWins.length > 0 || gameplay.pandasPaylineWins.length > 0);
        }

        if (winsToShow) {

            this.flow.addCallback(() => {

                if (spinAction === SpinAction.SPIN) {
                    this.model.state = GameState.SHOW_WINS;

                } else if (spinAction === SpinAction.VS_SPIN) {
                    this.model.state = GameState.VS_SPIN_SHOW_WINS;

                } else {
                    this.model.state = GameState.FREE_SPIN_SHOW_WINS;
                }

                this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, !this.model.bigWin);

                this.winSounds(accumulatedMoneyWon / betData.playerBet);

                if (!nextActions || (spinAction === SpinAction.FREE_SPIN && nextActions[0] !== GameRequest.VS_SPIN)) {
                    this.winLineLoopDragons(true);
                }
            });
        }

        if (this.gameUI && !this.model.bigWin && (winsToShow || this.model.reels.jackpotSymbols)) {
            if (this.model.reels.jackpotSymbols) {
                this.flow.addSignalBlock(this.jackpotBowl.signals.winComplete);
            } else {
                this.flow.addSignalBlock(this.gameUI.signals.winRollupComplete);
            }
        }

        if (nextActions) {
            const { vsSymbols, freeSpinSymbols } = this.model.reels;

            if (vsSymbols) {
                this.flow.addCallback(() => {
                    this.reels.bonusWinAnimations(vsSymbols);
                });
            } if (spinAction !== SpinAction.FREE_SPIN && freeSpinSymbols) {
                this.flow.addCallback(() => {
                    this.reels.bonusWinAnimations(freeSpinSymbols);
                });
            }
        }

        if (spinAction === SpinAction.FREE_SPIN) {
            this.flow.addDelay(this.services.resources.getSetting(`freeSpinPostWinsDelay`));
        }
    }

    private winSounds(winBetRatio: number): void {
        // regulatory requirement to not play winSounds when winRatio is <= 1 but to play the consolation sound.
        // DvsP only has sounds for >= 2 anyway, so just play consolation to cover the gap <2
        if (winBetRatio >= 2) {
            const bestSymbolIndex = this.reels.getWinSymbols(this.model.reels.winningSymbolState);
            this.soundplayer.win(bestSymbolIndex);
        } else {
            this.soundplayer.consolation();
        }
    }

    private winLineLoopDragons(firstLine: boolean): void {

        if (this.model.winLineCount === 0 && !this.model.hasScatterWins) {

            return;
        }

        if (firstLine) {

            this.reels.startWinAnims(this.model.reels.winningSymbolState);
        }

        // SCATTER WIN LOGIC
        const dragonsScatterWinData = this.model.winlines.dragons.nextScatterWinData();
        const pandasScatterWinData = this.model.winlines.pandas.nextScatterWinData();
        if (dragonsScatterWinData && pandasScatterWinData) {
            this.winlinesDragons.showScatterWin(dragonsScatterWinData.symbols, true, dragonsScatterWinData.bonusWinnings, 0);
            this.winlinesPandas.showScatterWin(pandasScatterWinData.symbols, true, pandasScatterWinData.bonusWinnings, 0, true);

            if (this.model.winLineCount === 0) {
                this.model.winlines.dragons.resetLineIndex();
                this.model.winlines.pandas.resetLineIndex();

                if (!firstLine) {

                    this.signals.winlineLoopComplete.dispatch();
                }
            }

            return;
        }

        const winLineData = this.model.winlines.dragons.nextWinLineData();

        if (winLineData) {

            this.winlinesDragons.showWinLine(winLineData, this.model.timeBetweenWinsActive);
        } else {

            this.model.winlines.dragons.resetLineIndex();
            this.winLineLoopPandas();
        }
    }

    private winLineLoopPandas(): void {

        const winLineData = this.model.winlines.pandas.nextWinLineData();

        if (winLineData) {
            this.winlinesPandas.showWinLine(winLineData, this.model.timeBetweenWinsActive);
        } else {

            this.signals.winlineLoopComplete.dispatch();
            this.model.winlines.pandas.resetLineIndex();
            this.winLineLoopDragons(false);
        }
    }

    private addJackpotToFlow(event: SpinEvent | VSSpinEvent | FreeSpinEvent, inVS: boolean = false): void {

        const { jackpotSymbols } = this.model.reels;

        if (jackpotSymbols) {

            this.flow.addCallback(() => {

                if (this.model.bigWin && !inVS) {
                    this.reelEffects.enable();
                }

                this.soundplayer.bonusBell();

                this.winlinesDragons.showScatterWin(jackpotSymbols.dragons, undefined, undefined, undefined, true);
                this.winlinesPandas.showScatterWin(jackpotSymbols.pandas, undefined, undefined, undefined, true);

                this.reels.startScatterWinAnimationLoops(jackpotSymbols, false);
            })
                .addSignalBlock(this.reels.signals.scatterWinComplete)
                .addCallback(() => {
                    this.winlinesDragons.hideAllWinBoxes();
                    this.winlinesPandas.hideAllWinBoxes();

                    if (this.model.bigWin) {
                        this.soundplayer.bigWinCountUp();
                    } else {
                        const { balance, balanceVersion } = event.body;
                        const { accumulatedMoneyWon } = event.body.data;

                        this.updateGameUIWinnings(accumulatedMoneyWon, balance, balanceVersion, true, true, true);
                        this.setSkip(this.signals.skipJackpot);
                    }

                    this.jackpotBowl.startWinLoop();
                });
        }
    }

    private addVSWinToFlow(): void {

        const { vsSymbols, doubleBonus } = this.model.reels;
        const gameHistory = this.model.vsHistoryData;

        if (vsSymbols) {

            this.flow.addCallback(() => {

                this.stopWinAnims();

                this.winlinesDragons.showScatterWin(vsSymbols.dragons, undefined, undefined, undefined, true);
                this.winlinesPandas.showScatterWin(vsSymbols.pandas, undefined, undefined, undefined, true);

                this.reels.startScatterWinAnimationSequences(vsSymbols);

                if (doubleBonus) {

                    this.soundplayer.fgMusic();
                }

                this.soundplayer.fgMusicMute();
                this.soundplayer.vsMusic();
                this.soundplayer.bonusBell();
            })
                .addSignalBlock(this.reels.signals.scatterWinComplete)
                .addCallback(() => {
                    this.gameUI?.stopAutoPlay();
                    this.vsTransition.playFadeTransition();
                    this.vsTransition.playDragonPandaIntro();
                    this.setSkip(this.signals.skipTransition);
                })
                .addSignalBlock(this.vsTransition.signals.animationInComplete)
                .addCallback(() => {
                    this.winlinesDragons.hideAllWinBoxes();
                    this.winlinesPandas.hideAllWinBoxes();

                    this.vsScreen.show(gameHistory);
                    this.setVSGameMode();
                })
                .addSignalBlock(this.vsTransition.signals.animationOutComplete)
                .addCallback(() => {
                    this.clearSkip();

                    this.soundplayer.vsIntro();
                    this.soundplayer.summaryWindowIn();
                    this.soundplayer.vsPickPromptLoop();
                    this.vsScreen.enable();
                    this.jackpotBowl.setInteractive(false);
                });
        }
    }

    private addFreeSpinsWinToFlow(): void {

        const { freeSpinSymbols } = this.model.reels;

        if (freeSpinSymbols) {

            this.flow.addCallback(() => {

                this.stopWinAnims();

                this.winlinesDragons.showScatterWin(freeSpinSymbols.dragons, undefined, undefined, undefined, true);
                this.winlinesPandas.showScatterWin(freeSpinSymbols.pandas, undefined, undefined, undefined, true);

                this.reels.startScatterWinAnimationSequences(freeSpinSymbols);

                this.soundplayer.fgMusic();
                this.soundplayer.bonusBell();
            })
                .addSignalBlock(this.reels.signals.scatterWinComplete)
                .addCallback(() => {
                    this.winlinesDragons.hideAllWinBoxes();
                    this.winlinesPandas.hideAllWinBoxes();
                });

            this.addSetFreeSpinsGameModeToFlow(true, false);
        }
    }

    private addVSTransitionToFlow(enteringFG: boolean = false): void {
        const { result, pick } = this.model.vsResultData as VSResultData;

        this.flow.addCallback(() => {
            if ((result === VSResult.DRAGON_WIN && pick === VSOption.DRAGON) || (result === VSResult.PANDA_WIN && pick === VSOption.PANDA)) {
                if (result === VSResult.DRAGON_WIN) {
                    this.soundplayer.vsMusicTermDragon();
                    this.vsTransition.playDragonWin(1400);
                } else {
                    this.soundplayer.vsMusicTermPanda();
                    this.vsTransition.playPandaWin(1400);
                }
                this.vsTransition.playFadeTransition(1400);
                this.setSkip(this.signals.skipTransition);
            } else {
                if (result === VSResult.DRAGON_WIN) {
                    this.soundplayer.vsMusicTermDragon();
                } else {
                    this.soundplayer.vsMusicTermPanda();
                }
                this.vsTransition.playCloseTransition();
                this.setSkip(this.signals.skipTransition);
            }
        })
            .addSignalBlock(this.vsTransition.signals.animationInComplete)
            .addCallback(() => {

                if (enteringFG) {
                    this.soundplayer.fgMusicUnmute();
                }
            });
    }

    private addRestoreScatterWinToFlow(totalMoneyWon: number, restoreBaseReels: boolean = true, restoreFreeGames: boolean = false): void {

        this.flow.addCallback(() => {

            this.stopWinAnims();

            const bonusTriggeringState = restoreFreeGames ? this.model.reels.bonusTriggeringState.freeGamesState : this.model.reels.bonusTriggeringState;

            if (bonusTriggeringState) {

                const { symbols, oldSymbols, overlays, paylines, multipliedWinsMap, multipliedWilds } = bonusTriggeringState;
                const overlayPositions = this.model.reels.getBonusOverlaySymbolPositions(restoreFreeGames);
                this.model.winlines.dragons.storeWins(paylines.dragons, multipliedWinsMap.dragons);
                this.model.winlines.pandas.storeWins(paylines.pandas, multipliedWinsMap.pandas);
                this.model.winlines.dragons.storeScatterWin(overlayPositions.dragons, totalMoneyWon);
                this.model.winlines.pandas.storeScatterWin(overlayPositions.pandas, totalMoneyWon);
                this.model.reels.setWinningSymbolState(paylines.dragons, paylines.pandas);
                this.model.reels.setJumpingWilds(oldSymbols.dragons, symbols.dragons, oldSymbols.pandas, symbols.pandas, multipliedWilds.dragons, multipliedWilds.pandas);
                this.reels.setReelSymbols(oldSymbols.dragons, oldSymbols.pandas, overlays);
                this.addJumpingWildsToFlow(true);

                if (this.vsTransition.getInAnim()) {
                    this.flow.addSignalBlock(this.vsTransition.signals.animationOutComplete);
                }

                this.flow.addCallback(() => {
                    this.winLineLoopDragons(true);
                    if (overlayPositions) {
                        this.reels.startScatterWinAnimationLoops(overlayPositions);
                    }
                });
            } else {

                const { dragonsDefaultReels, pandasDefaultReels } = this.model.openGameData;
                this.reels.setReelSymbols(dragonsDefaultReels, pandasDefaultReels, this.model.reels.overlaySymbolEmptyState);
            }

            if (restoreBaseReels) {
                this.setBaseGameModeVisuals();
            }
        });
    }

    private addSetFreeSpinsGameModeToFlow(entering: boolean, fromVS: boolean, resuming: boolean = false): void {

        this.flow.addCallback(() => {

            this.stopWinAnims();

            let setWithDefaults = true;

            if (!entering) {

                this.reels.setupFreeGamesFrames();
                this.winlinesCountDragons.disableFlames();
                this.winlinesCountPandas.disableFlames();

                const bonusTriggeringState = this.model.reels.bonusTriggeringState;

                if (bonusTriggeringState) {

                    const { symbols, overlays } = bonusTriggeringState;
                    this.setFreeSpinsGameMode(symbols.dragons, symbols.pandas, overlays, entering);

                    setWithDefaults = false;
                }
            }

            if (setWithDefaults) {
                const { dragonsFreeGamesReels, pandasFreeGamesReels } = this.model.openGameData;
                this.setFreeSpinsGameMode(dragonsFreeGamesReels, pandasFreeGamesReels, this.model.reels.overlaySymbolEmptyState, entering);
            }
        });

        if (entering && fromVS) {

            this.flow.addCallback(() => {

                const { dragonsFreeGamesReels, pandasFreeGamesReels } = this.model.openGameData;
                this.reels.setReelSymbols(dragonsFreeGamesReels, pandasFreeGamesReels, this.model.reels.overlaySymbolEmptyState);
                this.freeSpinCounter.appear();

                this.reels.setupFreeGamesFrames();
                this.winlinesCountDragons.disableFlames();
                this.winlinesCountPandas.disableFlames();

                this.reelTopDisplay.setBonusGameMode();

                this.setSkip(this.signals.skipFreeSpinPrompt);
            })
                .addSignalBlock(this.freeSpinsPrompt.signals.complete)
                .addCallback(() => {
                    this.clearSkip();
                });
        } else if (entering) {

            this.flow.addCallback(() => {

                this.setSkip(this.signals.skipFreeSpinPrompt);
            })
                .addSignalBlock(this.freeSpinsPrompt.signals.complete)
                .addCallback(() => {
                    this.gameUI?.stopAutoPlay();
                    this.vsTransition.playCloseTransition();
                    this.setSkip(this.signals.skipTransition);
                })
                .addSignalBlock(this.vsTransition.signals.animationInComplete)
                .addCallback(() => {

                    const { dragonsFreeGamesReels, pandasFreeGamesReels } = this.model.openGameData;
                    this.reels.setReelSymbols(dragonsFreeGamesReels, pandasFreeGamesReels, this.model.reels.overlaySymbolEmptyState);
                    this.freeSpinCounter.appear();

                    this.reels.setupFreeGamesFrames();
                    this.winlinesCountDragons.disableFlames();
                    this.winlinesCountPandas.disableFlames();

                    this.reelTopDisplay.setBonusGameMode();
                })
                .addSignalBlock(this.vsTransition.signals.animationOutComplete)
                .addCallback(() => {
                    this.clearSkip();
                });
        }
    }

    private setBaseGameMode(dragonsSymbols: ReelSetSymbols, pandasSymbols: ReelSetSymbols, overlays: OverlaySymbolState): void {

        const { dragonsDefaultReelStrips, pandasDefaultReelStrips } = this.model.openGameData;

        this.reels.setReelStrips(dragonsDefaultReelStrips, pandasDefaultReelStrips);
        this.reels.setReelSymbols(dragonsSymbols, pandasSymbols, overlays);

        this.setBaseGameModeVisuals();
    }

    private setBaseGameModeVisuals(): void {

        this.background.setBase();
        this.darkenOverlay.disable();
        this.resultsDarkenOverlay.disable();
        this.reelTopDisplay.setBaseGameMode();
        this.vsResultPanelDragons.hide();
        this.vsResultPanelPandas.hide();
        this.reelTopDragonPanda.clearTints();
        this.freeSpinCounter.reset();
        this.reels.setupBaseFlames();
        this.reels.setupNeonDividers();
        this.winlinesCountDragons.enableFlames();
        this.winlinesCountPandas.enableFlames();
        this.fgTotalPanel.reset();
        this.reelTopDisplay.disableVSEffects();
        this.reelEffects.disable();
    }

    /*
        Unlike base + freespins which may need to restore a previous state to the reels,
        VS default symbols are always set here as it is not returned to from any other state.
    */
    private setVSGameMode(): void {

        const { dragonsVsFreeGameReelStrips, pandasVsFreeGameReelsStrips, dragonsVsFreeGameReels, pandasVsFreeGameReels } = this.model.openGameData;

        this.reels.setReelStrips(dragonsVsFreeGameReelStrips, pandasVsFreeGameReelsStrips);
        this.reels.setReelSymbols(dragonsVsFreeGameReels, pandasVsFreeGameReels, this.model.reels.overlaySymbolEmptyState);

        this.background.setVS();
        this.reelTopDisplay.setBonusGameMode();
        this.freeSpinCounter.visible = false;
    }

    private setFreeSpinsGameMode(dragonsSymbols: ReelSetSymbols, pandasSymbols: ReelSetSymbols, overlays: OverlaySymbolState,
        entering: boolean): void {

        const { dragonsFreeGamesReelsStrips, pandasFreeGamesReelStrips } = this.model.openGameData;

        this.reels.setReelStrips(dragonsFreeGamesReelsStrips, pandasFreeGamesReelStrips);

        this.background.setBase();
        this.darkenOverlay.disable();
        this.resultsDarkenOverlay.disable();
        this.vsResultPanelDragons.hide();
        this.vsResultPanelPandas.hide();
        this.reelTopDragonPanda.clearTints();
        this.reelTopDisplay.disableVSEffects();

        if (entering) {
            this.freeSpinsPrompt.show();
        } else {
            this.reels.setReelSymbols(dragonsSymbols, pandasSymbols, overlays);
            this.freeSpinCounter.appear();
            this.reelTopDisplay.setBonusGameMode();
        }
    }

    private addSetFreeSpinsToFlow(freeSpinsCounter?: FreeSpinsCounter): void {

        if (freeSpinsCounter) {

            this.flow.addCallback(() => {
                this.freeSpinCounter.setFreeSpins(freeSpinsCounter.freeSpinsRemaining, freeSpinsCounter.freeSpinsTotal);
            });
        }
    }

    private addCountUpToTotalFreeSpinsToFlow(freeSpinsCounter?: FreeSpinsCounter): void {

        if (freeSpinsCounter) {

            if (this.vsTransition.getInAnim()) {

                this.flow.addSignalBlock(this.vsTransition.signals.animationOutComplete);
            }

            this.flow.addCallback(() => {

                this.freeSpinCounter.appear();
                this.freeSpinCounter.countUpToTotalFreeSpins(freeSpinsCounter.freeSpinsTotal, freeSpinsCounter.freeSpinsTotal);
                this.setSkip(this.signals.skipFreeSpinCounter, freeSpinsCounter.freeSpinsTotal);
            })
                .addSignalBlock(this.freeSpinCounter.signals.totalCountUpComplete)
                .addCallback(() => this.clearSkip());
        }
    }

    private addNextActionsHandlingToFlow(betData: BetData, accumulatedMoneyWon: number, netPosition: NetPosition | undefined,
        balance: number, balanceVersion: number, inBonus: boolean, nextActions?: GameRequest[]): void {

        if (nextActions && nextActions[0] === GameRequest.VS_SPIN) {
            this.flow.addSignalBlock(this.signals.vsChosen);
        }
        this.flow.addCallback(() => {

            if (nextActions) {

                this.stopWinAnims();

                if (nextActions[0] === GameRequest.VS_SPIN) {
                    this.client.sendVSSpinRequest(betData, this.model.vsPickIndex as VSOption, this.model.freeRoundId);
                } else {
                    this.client.sendFreeSpinRequest(betData, this.model.freeRoundId);
                }
            } else {

                this.updateBalance(balance, balanceVersion);
                if (netPosition) {
                    this.gameUI?.updateNetProfit(netPosition.netProfit);
                }

                if (inBonus) {
                    this.model.setSpinCompleteWaiter(accumulatedMoneyWon);
                } else {
                    this.gameUI?.spinComplete(accumulatedMoneyWon);
                }

                this.model.state = GameState.IDLE;
                this.reelTopDisplay.setIdle();
                this.model.haveEnteredFreeSpins = false;
            }
        });
    }

    private onTransitionComplete(): void {
        this.vsScreen.flashLoop();

        if (!this.model.enteringVS) this.jackpotBowl.setInteractive(true);

        const spinCompleteWaiter: SpinCompleteWaiter = this.model.spinCompleteWaiter;
        if (spinCompleteWaiter.waiting) {
            this.model.clearSpinCompleteWaiter();
            this.gameUI?.spinComplete(spinCompleteWaiter.accumulatedMoneyWon);
        }
    }

    private slamStop(dragonsSpinResult: ReelSetSymbols, pandasSpinResult: ReelSetSymbols): void {

        const { overlaySymbolState } = this.model.reels;

        this.reels.slamStopAllReels(dragonsSpinResult, pandasSpinResult, overlaySymbolState);

        this.soundplayer.reelStop();
    }

    private slamStopDragonsReels(dragonsSpinResult: ReelSetSymbols): void {

        const { overlaySymbolState } = this.model.reels;

        this.reels.slamStopDragonsReels(dragonsSpinResult, overlaySymbolState.dragons);

        this.soundplayer.reelStop();
    }

    private slamStopPandasReels(pandasSpinResult: ReelSetSymbols): void {

        const { overlaySymbolState } = this.model.reels;

        this.reels.slamStopPandasReels(pandasSpinResult, overlaySymbolState.dragons);

        this.soundplayer.reelStop();
    }

    private skipFreeSpinPrompt(): void {
        this.services.playerInactivity.stop();
        this.freeSpinsPrompt.hide();
    }

    private skipFreeSpinCounter(total: number): void {
        this.freeSpinCounter.skip(total);
    }

    private skipJackpot(): void {
        this.jackpotBowl.skipJackpot();
    }

    private skipBigWin(): void {

        this.bigWin.signals.skip.dispatch();
        this.soundplayer.stopBigWinCountUp();
    }

    private skipWinRollUp(): void {
        this.gameUI?.skipWinRollup();
        this.soundplayer.stopWinRollUp(this.model.bigWin, this.model.inFreeSpinBonus);
    }

    private skipTransition(): void {
        this.soundplayer.skipTransition();
        this.vsTransition.skip();
    }

    private skipTotalPanel(): void {
        this.fgTotalPanel.skip();
    }
    private skipResultPanel(): void {
        this.vsResultPanelDragons.skip();
        this.vsResultPanelPandas.skip();
        this.reelTopDragonPanda.clearTints();
    }

    private stopWinAnims(): void {

        this.winlinesDragons.reset();
        this.winlinesPandas.reset();
        this.reels.stopWinAnims();
    }

    /**
    *
    * @param dt Seconds
   */
    private tick(dt: number): void {

        this.reelTopDisplay.tick(dt);
    }

    private resize(): void {

        const { layout } = this.services;

        this.freeSpinCounter.resize(layout.orientation);
        this.ksgLogo.resize(this.gameUI?.gameSafeArea);
        this.portraitLogo.resize(layout.orientation);
        this.vsScreen.resize();
    }

    private spinError(event: SpinErrorEvent): void {
        this.gameUI?.spinCancelled();
        switch (event.body.data.code) {
            case `OUT_OF_MONEY`:
                showInsufficientFundsDialog(this.services, this.gameDialogs);
                break;
            case `FREE_ROUND_FINISHED`:
                showActivateFreeRoundFinishedDialog(this.services, this.gameDialogs, this.resetGame);
                break;
            case `GAMING_LIMITS_REACHED`:
                showGamingLimitsReachedErrorDialog(this.services, this.gameDialogs);
                break;
            default:
                console.warn(`Unexpected Spin Error`, event);
                showGenericCommsErrorDialog(this.services, this.gameDialogs, this.resetGame);
                break;
        }
    }

    private setupGameUI(event: OpenGameEvent, resumeData?: LastEventData, lastRoundId?: string): void {

        if (this.gameUI) {

            const { balance, balanceVersion, lastEvent, netPosition } = event.body;
            const { betConfigs, betConfig, defaultBetType } = event.body.data;

            let selectedBetConfigName = defaultBetType;
            let selectedCoin: number;
            let uiBetConfigs: UIBetConfig<BetConfigKey>[];

            if (betConfigs) {
                selectedCoin = betConfigs[selectedBetConfigName].defaultCoinSize;
                uiBetConfigs = [
                    { ...betConfigs.SMALL_SPIN, configName: `SMALL_SPIN` },
                    { ...betConfigs.SPIN, configName: `SPIN` },
                ];
            }
            else {
                selectedCoin = betConfig!.defaultCoinSize;
                uiBetConfigs = [
                    { ...betConfig!, configName: `SPIN` }
                ];
            }


            if (resumeData) {
                selectedCoin = resumeData.betData.coinSize;
                const resumedBetConfig = uiBetConfigs.find(config => config.coinSizes.includes(selectedCoin) && config.coinsPerBet === resumeData.betData.coinsPerBet);
                if (!resumedBetConfig) {
                    throw new Error(`No resumed bet config found`);
                }
                selectedBetConfigName = resumedBetConfig.configName;
            }

            this.gameUI.init({
                betConfigs: uiBetConfigs,
                selectedBetConfigName,
                selectedCoin,
                balance,
                balanceVersion,
                netProfit: (netPosition) ? netPosition.netProfit : 0,
                inBrokenGame: lastEvent !== undefined,
                brokenGameBet: lastEvent ? lastEvent.data.betData.playerBet : undefined,
                win: resumeData?.accumulatedMoneyWon,
                rtp: this.model.rtp
            });
            this.ksgLogo.resize(this.gameUI?.gameSafeArea);

            const startingConfig = betConfigs ? betConfigs[selectedBetConfigName] : betConfig!;
            this.services.paytableContentFactory.updateJackpotPayouts(this.services, this.model.openGameData, selectedCoin, startingConfig.coinsPerBet);
            this.gameUI.signals.betChanged.add(bet => {
                this.services.paytableContentFactory.updateJackpotPayouts(this.services, this.model.openGameData, bet.selectedCoin, bet.coinsPerBet);
                this.services.paytableContentFactory.updateSubRTPVariant(bet);

                //@ts-ignore
                this.gameUI.topBar.updateMaxWinInfo();
                this.noBonusMessage.display(bet.coinsPerBet);
            });

            this.gameUI.signals.startSpin.add((spinData: SpinData) => {

                this.stopWinAnims();

                this.model.state = GameState.SPIN;
                this.model.spinSpeedType = spinData.spinSpeed;
                this.model.spinModeType = spinData.spinMode;

                this.reelTopDisplay.setSpin();

                const betData: BetData = {
                    coinsPerBet: spinData.coinsPerBet,
                    coinSize: spinData.selectedCoin,
                    playerBet: spinData.totalBet,
                    pickIndex: null
                };
                if (spinData.betConfigName as BetConfigKey === `SMALL_SPIN`) {
                    this.client.sendSmallSpinRequest(betData, this.model.freeRoundId);
                } else {
                    this.client.sendSpinRequest(betData, this.model.freeRoundId);
                }
            });

            this.gameUI.signals.spinModeChanged.add((spinMode) => {
                this.reelTopDisplay.setSpinModeChanged(spinMode);
            });

            this.gameUI.signals.openDeposit.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `goToDeposit` }));
            this.gameUI.signals.playForReal.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `playCashMode` }));
            this.gameUI.signals.openGameHistory.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `openGameHistory` }));
            this.gameUI.signals.goToHome.add(() => this.services.gameBridge.sendGameBridgeEvent({ event: `showLobby` }));
            this.gameUI.signals.sounds.click.add(() => this.soundplayer.uiClick());
            this.gameUI.signals.sounds.spinClick.add(() => this.soundplayer.uiClick());
            this.gameUI.signals.winRollUpSkipEnable.add(() => this.setSkip(this.signals.skipWinRollUp));
            this.gameUI.signals.winRollupComplete.add(() => this.clearSkip());

            this.syncSessionElapsedTimeIfExists(event.body.elapsedTimeInMillis);

            if (lastRoundId) {
                this.gameUI?.updateTransactionId(lastRoundId);
            }
        }
    }

    private updateGameUIWinnings(accumulatedMoneyWon: number, balance: number, balanceVersion: number, playSound: boolean = true, updateBalance: boolean = true, miniJackpot: boolean = false): void {

        if (this.gameUI) {

            const duration = this.gameUI.setWin(accumulatedMoneyWon);

            if (updateBalance) {
                this.updateBalance(balance, balanceVersion);
            }

            if (playSound && duration > 0) {
                this.soundplayer.winRollUp(duration, this.model.inFreeSpinBonus, miniJackpot);
            }
        }
    }

    private setSkip<SignalType extends Signal<(...args: Parameters<SignalType["dispatch"]>) => void>>(signal: SignalType, ...args: Parameters<SignalType["dispatch"]>): void {
        this.gameUI?.signals.skip.once(() => signal.dispatch(...args));
        this.gameUI?.showSkipControls();
    }

    private clearSkip(): void {
        this.gameUI?.signals.skip.detachAll();
        this.gameUI?.hideSkipControls();
    }

    private periodicInspection(event: PeriodicInspectionEvent) {
        this.updateBalance(event.body.balance, event.body.balanceVersion);
        if (event.body.netPosition) {
            this.gameUI?.updateNetProfit(event.body.netPosition.netProfit);
        }
        this.gameUI?.syncSessionElapsedTime(event.body.elapsedTimeInMillis);
    }

    private updateBalance(balance: number, balanceVersion: number) {
        if (this.gameUI?.updateBalance(balance, balanceVersion)) {
            this.services.gameBridge.sendGameBridgeEvent({ event: `setBalance`, payload: { balance: balance / this.services.currency.currencyConfig.minorDivisor } });
        }
    }

    private syncSessionElapsedTimeIfExists(elapsedTimeInMillis?: number): void {

        if (elapsedTimeInMillis !== undefined) {
            this.gameUI?.syncSessionElapsedTime(elapsedTimeInMillis);
        }
    }

    private addSessionCompleteGameBridgeToFlow(nextActions?: GameRequest[]) {
        if (!nextActions) {
            this.flow.addCallback(() => {
                this.services.gameBridge.sendGameBridgeEvent({ event: `gameNotBusy` });
                this.services.gameBridge.sendGameBridgeEvent({ event: `gameActivity` });

                if (this.model.activeFreeRoundsFinished) {
                    const winAmount = this.model.activeFreeRoundData.data.winAmount;
                    const formattedWinAmount = this.services.currency.formatCoins(winAmount, { group: true, addTrailingZeros: true, trimIntegers: true });
                    this.gameDialogs.show({
                        type: GameDialogType.FREE_ROUNDS_SUMMARY,
                        formattedWinAmount,
                        accept: () => {
                            this.model.freeRoundsComplete();
                            this.gameUI?.freeRoundsCompleted();
                        }
                    });

                }
            });

        }
    }

    private onReels(): boolean {
        if (this.model.state !== GameState.INIT && !this.vsTransition.getInAnim() && !this.model.enteringVS) {
            return true;
        }

        return false;
    }
}